import { PriceUtils } from "../service/price-utils"

export interface BikeForSaleMarginInput {
  acquisitionCostKr: number
  estimatedSalePriceKr: number
  servicePartsAcquisitionCostKr: number
  laborTimeMinutesTotal: number
  hourlyCostRateKrPerHour: number
  requiredMarginPercent: number
}

export type BikeForSaleMarginResult =
  | {
      kind: "NOT_COMPUTABLE"
      reason: "ZERO_PRICE" | "NEGATIVE_PROFIT_BAD_ECONOMICS"
      serviceCostKr: number
    }
  | {
      kind: "COMPUTED"
      serviceCostKr: number
      vatKr: number
      profitKr: number
      marginPercent: number
      meetsRequirement: boolean
    }

export class BikeForSaleMargin {
  // Bikes are sold with standard Swedish 25% VAT included in the sale price.
  // Cost inputs are entered excl. VAT.
  static readonly VAT_RATE_PERCENT = 25

  static computeServiceCostKr(
    servicePartsAcquisitionCostKr: number,
    laborTimeMinutesTotal: number,
    hourlyCostRateKrPerHour: number,
  ): number {
    const laborKr = (laborTimeMinutesTotal / 60) * hourlyCostRateKrPerHour
    return servicePartsAcquisitionCostKr + laborKr
  }

  static compute(input: BikeForSaleMarginInput): BikeForSaleMarginResult {
    const {
      acquisitionCostKr,
      estimatedSalePriceKr,
      servicePartsAcquisitionCostKr,
      laborTimeMinutesTotal,
      hourlyCostRateKrPerHour,
      requiredMarginPercent,
    } = input

    const serviceCostKr = BikeForSaleMargin.computeServiceCostKr(
      servicePartsAcquisitionCostKr,
      laborTimeMinutesTotal,
      hourlyCostRateKrPerHour,
    )

    return BikeForSaleMargin.computeFromServiceCost({
      acquisitionCostKr,
      estimatedSalePriceKr,
      serviceCostKr,
      requiredMarginPercent,
    })
  }

  static computeFromServiceCost(input: {
    acquisitionCostKr: number
    estimatedSalePriceKr: number
    serviceCostKr: number
    requiredMarginPercent: number
  }): BikeForSaleMarginResult {
    const {
      acquisitionCostKr,
      estimatedSalePriceKr,
      serviceCostKr,
      requiredMarginPercent,
    } = input

    if (estimatedSalePriceKr === 0) {
      return { kind: "NOT_COMPUTABLE", reason: "ZERO_PRICE", serviceCostKr }
    }

    if (acquisitionCostKr >= estimatedSalePriceKr) {
      return {
        kind: "NOT_COMPUTABLE",
        reason: "NEGATIVE_PROFIT_BAD_ECONOMICS",
        serviceCostKr,
      }
    }

    const vatKr =
      estimatedSalePriceKr *
      (BikeForSaleMargin.VAT_RATE_PERCENT /
        (100 + BikeForSaleMargin.VAT_RATE_PERCENT))
    const netRevenueKr = estimatedSalePriceKr - vatKr
    const profitKr = netRevenueKr - acquisitionCostKr - serviceCostKr
    const marginPercent = (profitKr / netRevenueKr) * 100
    const meetsRequirement = marginPercent >= requiredMarginPercent

    return {
      kind: "COMPUTED",
      serviceCostKr,
      vatKr,
      profitKr,
      marginPercent,
      meetsRequirement,
    }
  }

  static formatKr(value: number): string {
    return PriceUtils.format(Math.round(value))
  }
}
